import { FC, ReactNode } from 'react'
import Link from 'next/link'
import { Box, Typography } from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { GRAY_COLORS_VARIANTS } from '@/utils/constants/colors'

// Lucide "flame" icon (matches the Lovable demo's featured badge).
const FlameIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0 }}
  >
    <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
  </svg>
)

// Hardcoded accent used for the runtime-generated dot pattern and the glow.
// (Kept hardcoded for now; can be promoted to a partner variable later.)
const ACCENT_COLOR = '#36E0F6'
const ACCENT_SOFT = 'rgba(54, 224, 246, 0.2)'

export interface HighlightedCardProps {
  /** Where the card links to when clicked. */
  href: string
  /** Prominent title (rendered italic + uppercase, matching the design). */
  title: string
  /** Optional supporting line under the title. */
  subtitle?: ReactNode
  /** Optional logo shown on the left (partner logo). */
  logoSrc?: string
  /** Small badge label, e.g. "Featured". */
  badgeLabel?: string
  /** Card background (base) colour. */
  baseColor: string
  /** Badge background colour. */
  badgeColor: string
  /** Badge text colour. */
  badgeTextColor: string
  /** Title colour. */
  titleColor: string
  /** Subtitle colour. */
  subtitleColor?: string
  /** Optional font family for the title. */
  fontFamily?: string
}

export const HighlightedCard: FC<HighlightedCardProps> = ({
  href,
  title,
  subtitle,
  logoSrc,
  badgeLabel = 'Featured',
  baseColor,
  badgeColor,
  badgeTextColor,
  titleColor,
  subtitleColor = 'rgba(255, 255, 255, 0.8)',
  fontFamily,
}) => {
  return (
    <Link href={href} style={{ textDecoration: 'none', display: 'block', width: '100%' }}>
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: '1rem',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.35)',
          cursor: 'pointer',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 14px 32px rgba(0, 0, 0, 0.45)',
          },
        }}
      >
        {/* Base colour */}
        <Box sx={{ position: 'absolute', inset: 0, backgroundColor: baseColor }} />
        {/* Runtime-generated dot pattern */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0.2,
            backgroundImage: `radial-gradient(circle at 20% 20%, ${ACCENT_COLOR} 1px, transparent 1px), radial-gradient(circle at 80% 80%, ${ACCENT_COLOR} 1px, transparent 1px)`,
            backgroundSize: '40px 40px',
          }}
        />
        {/* Glow blob */}
        <Box
          sx={{
            position: 'absolute',
            top: -32,
            right: -32,
            width: 160,
            height: 160,
            borderRadius: '50%',
            backgroundColor: ACCENT_SOFT,
            filter: 'blur(48px)',
          }}
        />
        {/* Content */}
        <Box
          sx={{
            position: 'relative',
            p: 2,
            minHeight: 108,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {logoSrc && (
            <img
              src={logoSrc}
              alt=""
              style={{
                width: 56,
                height: 56,
                objectFit: 'contain',
                flexShrink: 0,
                filter: 'drop-shadow(0 0 12px rgba(54, 224, 246, 0.5))',
              }}
            />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {badgeLabel && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  backgroundColor: badgeColor,
                  color: badgeTextColor,
                  fontSize: '10px',
                  fontWeight: 900,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  px: 1,
                  py: 0.25,
                  borderRadius: '4px',
                  mb: 0.5,
                }}
              >
                <FlameIcon />
                {badgeLabel}
              </Box>
            )}
            <Typography
              component="h2"
              sx={{
                fontSize: { xs: '1.125rem', md: '1.5rem' },
                fontWeight: 900,
                fontStyle: 'italic',
                textTransform: 'uppercase',
                lineHeight: 1.1,
                color: titleColor,
                fontFamily: fontFamily || "'Montserrat', sans-serif",
                whiteSpace: 'normal',
                wordBreak: 'break-word',
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  mt: 0.25,
                  color: subtitleColor,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          <ChevronRightIcon sx={{ color: GRAY_COLORS_VARIANTS[50], fontSize: 24, flexShrink: 0 }} />
        </Box>
      </Box>
    </Link>
  )
}
